import { useMemo, useRef, useState } from 'react'

import InfiniteScroll from 'react-infinite-scroller'

import { type Space, SpaceType } from '@tribeplatform/gql-client/types'
import { useSpaces } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { InlineMultiselect } from '@tribeplatform/react-ui-kit/Multiselect'

import { EntityImage } from '../common/components/EntityImage.js'
import { useDebounce } from '../common/hooks/index.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'

const INLINE_MULTI_SELECT_PAGE_SIZE = 10

interface SpacePathPickerProps {
  onSelect: (path: string) => void
}
export const SpacePathPicker = ({ onSelect }: SpacePathPickerProps) => {
  const { $t } = useI18n()
  const dataRef = useRef<Space[]>(undefined)
  const [searchInput, setSearchInput] = useState('')
  const query = useDebounce(searchInput, 500)

  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isInitialLoading,
  } = useSpaces({
    fields: {
      image: 'basic',
    },
    variables: {
      limit: INLINE_MULTI_SELECT_PAGE_SIZE,
      query: searchInput ? query : '',
      type: [SpaceType.Group],
    },
  })

  const spaces = useMemo(() => {
    const prevData = dataRef.current
    if (prevData && !data && isInitialLoading) {
      return prevData
    }

    if (!data && !prevData) {
      return []
    }

    const { nodes } = simplifyPaginatedResult<Space>(data)
    dataRef.current = nodes

    return nodes
  }, [data, isInitialLoading])

  return (
    <InlineMultiselect>
      <InlineMultiselect.SearchBar
        value={searchInput}
        placeHolder={$t({
          defaultMessage: 'Search...',
          id: 'Generics.SearchDotDotDot',
        })}
        onChange={setSearchInput}
      />
      <InlineMultiselect.Items isLoading={isInitialLoading}>
        {spaces.length === 0 && isInitialLoading && (
          <div className="text-sm text-content-subdued flex justify-center">
            <SvgIcon className="animate-spin" size="lg" name="spinner" />
          </div>
        )}
        {!isInitialLoading && spaces.length === 0 && (
          <div className="text-sm text-content-subdued">
            <T id="Generics.NoResults" defaultMessage="No results" />
          </div>
        )}
        <InfiniteScroll
          pageStart={0}
          loadMore={fetchNextPage}
          useWindow={false}
          hasMore={hasNextPage ?? false}
        >
          {spaces.map(space => (
            <div
              key={space.id}
              className="flex items-center gap-x-2 p-2 rounded-base cursor-pointer hover:bg-surface-hovered h-10"
              onClick={() => onSelect(space.relativeUrl)}
            >
              <div className="shrink-0">
                <EntityImage entity={space} size="lg" />
              </div>
              <div className="truncate text-base">{space.name}</div>
            </div>
          ))}
          {isFetchingNextPage && (
            <div className="flex justify-center">
              <SvgIcon className="animate-spin" size="lg" name="spinner" />
            </div>
          )}
        </InfiniteScroll>
      </InlineMultiselect.Items>
    </InlineMultiselect>
  )
}
